import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  FileTextIcon,
  FilePlusIcon,
  MailIcon,
  MessageSquareIcon
} from 'lucide-react';
import { AppBar } from '../components/AppBar';
import { ApprovalTimeline } from '../components/ApprovalTimeline';
import { usePortal } from '../contexts/PortalContext';
import { observationLetterUrl, submissionPdfUrl } from '../api/research';
import { IRB_EMAIL } from '../data/proposalContent';
import { PHASES, WIZARD_STEPS, stepPath } from '../data/wizardSteps';
import { APPROVAL_STAGES, stageIndex } from '../data/approvalWorkflow';
import { STATUS_TO_STAGE, formatDateTime, isDecided, isSentBack } from '../data/fieldMap';
import { firstNameOf } from '../data/roles';

export function Dashboard() {
  const {
    user,
    config,
    data,
    status,
    history,
    completedSteps,
    isRequired,
    loadingProposal,
    sessionToken,
    proposals,
    selectProposal,
    startNewProposal
  } = usePortal();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);

  /* A researcher submits many proposals over the years. The procedure reopens
     an existing unsubmitted draft rather than piling up empties — so when one
     exists, say "Continue draft" rather than promising a new one. */
  const openDraft = proposals.find((row) => !row.IsSubmitted);

  const handleNewProposal = async () => {
    setStarting(true);
    const code = await startNewProposal();
    setStarting(false);
    if (code) navigate(stepPath(WIZARD_STEPS[0].id));
  };

  const formSteps = WIZARD_STEPS;
  const satisfied = formSteps.filter(
    (step) => completedSteps.includes(step.id) || !isRequired(step.id)
  );
  const nextStep =
    formSteps.find((step) => !completedSteps.includes(step.id) && isRequired(step.id)) ??
    WIZARD_STEPS[WIZARD_STEPS.length - 1];

  const firstName = firstNameOf(user?.fullName);
  const irbEmail = config?.irbEmail ?? IRB_EMAIL;

  const submitted = Boolean(status?.isSubmitted) || (status?.statusId ?? '01') !== '01';
  const stageId = STATUS_TO_STAGE[status?.statusId ?? '01'] ?? 'submission';
  const stage = APPROVAL_STAGES[Math.max(0, stageIndex(stageId))];
  const remaining = submitted ? APPROVAL_STAGES.length - stageIndex(stageId) - 1 : APPROVAL_STAGES.length;

  /* Tracking panels mean nothing until something has been submitted, so a new
     researcher sees only the welcome, their drafts and the way in — as the
     WebForms Research.aspx showed them only the Add button and their list. */
  const hasSubmitted = proposals.some((row) => row.IsSubmitted || row.StatusId !== '01');

  const sentBack = isSentBack(status?.statusId ?? '');
  const decided = isDecided(status?.statusId ?? '');

  /* The most recent committee remark, which is what a returned proposal is about. */
  const lastRemark = history.find((row) => (row.Remarks ?? '').trim().length > 0);

  return (
    <div className="page">
      <AppBar showHome={false} />
      <main className="container section-gap">
        <div className="stack">
          <section className="hero">
            <div>
              <h1>Welcome back, {firstName || 'Researcher'}</h1>
              <p>
                {loadingProposal
                  ? 'Loading your proposal…'
                  : sentBack
                    ? `${status?.statusDescription}. Make the changes requested below, then submit again.`
                    : decided
                      ? status?.statusForResearcher
                      : submitted
                        ? `Your proposal is with the ${stage.label}. ${remaining} stage${
                            remaining === 1 ? '' : 's'
                          } remain before the approval letter is issued.`
                        : 'Complete your proposal section by section, then agree to the declaration and submit.'}
              </p>
            </div>
            <button
              type="button"
              className="btn btn--success"
              onClick={() => navigate(stepPath(nextStep.id))}
            >
              {completedSteps.length === 0
                ? 'Start proposal'
                : status && !status.canEdit
                  ? 'Open proposal'
                  : sentBack
                    ? 'Make the requested changes'
                    : `Continue — ${nextStep.label}`}
              <ArrowRightIcon size={16} aria-hidden="true" />
            </button>
          </section>

          {proposals.length > 0 ? (
            <section className="card">
              <div className="card-header">
                <div>
                  <h2 className="card-title">Your proposals</h2>
                  <p className="card-subtitle">
                    {proposals.length === 1
                      ? 'Everything you have submitted to the IRB.'
                      : `${proposals.length} proposals. Select one to view or continue it.`}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn--primary btn--small"
                  disabled={starting}
                  onClick={() => void handleNewProposal()}
                >
                  <FilePlusIcon size={14} aria-hidden="true" />
                  {starting ? 'Opening…' : openDraft ? 'Continue draft' : 'New proposal'}
                </button>
              </div>
              <div className="card-body">
                <div className="table-scroll">
                  <table className="data-table" style={{ minWidth: 700 }}>
                    <thead>
                      <tr>
                        <th scope="col">IRB no.</th>
                        <th scope="col" style={{ minWidth: 300 }}>
                          Title
                        </th>
                        <th scope="col">Status</th>
                        <th scope="col">Submitted</th>
                        <th scope="col" className="col-action">
                          Open
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposals.map((row) => {
                        const open = row.ResearchCode === status?.researchCode;
                        return (
                          <tr key={row.ResearchCode} className={open ? 'row-current' : undefined}>
                            <td>{row.IrbNumber ?? <span className="muted">Not yet issued</span>}</td>
                            <td>
                              {row.Title || row.ProposalTitle || (
                                <em className="muted">Untitled draft</em>
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  row.StatusId === '10'
                                    ? 'badge--green'
                                    : row.StatusId === '11' || isSentBack(row.StatusId)
                                      ? 'badge--red'
                                      : row.IsSubmitted
                                        ? 'badge--blue'
                                        : 'badge--grey'
                                }`}
                              >
                                {row.StatusDescription}
                              </span>
                            </td>
                            <td className="muted">{formatDateTime(row.SubmitDateTime)}</td>
                            <td className="col-action">
                              {open ? (
                                <span className="muted">Open</span>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn--secondary btn--small"
                                  onClick={() => void selectProposal(row.ResearchCode)}
                                >
                                  Open
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          ) : null}

          {hasSubmitted ? (
          <>
          <section className="status-strip" aria-label="Current approval status">
            <div className="status-cell">
              <span className="status-key">IRB number</span>
              <span className="status-value">{status?.irbNumber ?? 'Not yet generated'}</span>
            </div>
            <div className="status-cell">
              <span className="status-key">Status</span>
              <span
                className={`badge ${
                  decided
                    ? status?.statusId === '10'
                      ? 'badge--green'
                      : 'badge--red'
                    : sentBack
                      ? 'badge--red'
                      : submitted
                        ? 'badge--blue'
                        : 'badge--grey'
                }`}
              >
                {status?.statusDescription ?? 'Draft — not submitted'}
              </span>
            </div>
            <div className="status-cell">
              <span className="status-key">Submitted on</span>
              <span className="status-value">{formatDateTime(status?.submittedAt)}</span>
            </div>
            <div className="status-cell">
              <span className="status-key">Next decision point</span>
              <span className="status-value">{submitted ? stage.timing : 'After you submit'}</span>
            </div>
          </section>

          {sentBack && lastRemark ? (
            <section className="card">
              <div className="card-header">
                <div>
                  <h2 className="card-title">What the committee asked for</h2>
                  <p className="card-subtitle">
                    {lastRemark.ActionedBy} · {formatDateTime(lastRemark.ActionedOn)}
                  </p>
                </div>
                <span className="badge badge--red">Action needed</span>
              </div>
              <div className="card-body">
                <div className="notice notice--error">
                  <MessageSquareIcon size={18} aria-hidden="true" />
                  <span className="review-prose">{lastRemark.Remarks}</span>
                </div>
                {data.coverLetter.hasObservationLetter && sessionToken && status ? (
                  <a
                    className="btn btn--secondary"
                    style={{ marginTop: 'var(--space-3)' }}
                    href={observationLetterUrl(sessionToken, status.researchCode)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Read the observation letter
                  </a>
                ) : null}
              </div>
            </section>
          ) : null}

          <div className="dash-grid">
            <section className="card">
              <div className="card-header">
                <div>
                  <h2 className="card-title">Approval route</h2>
                  <p className="card-subtitle">
                    Where your proposal is now and every stage it still has to pass through.
                  </p>
                </div>
                <span className="badge badge--grey">IRB workflow</span>
              </div>
              <div className="card-body">
                <ApprovalTimeline currentStageId={stageId} active={submitted} />
                {!submitted ? (
                  <p className="muted" style={{ marginTop: 'var(--space-4)' }}>
                    Tracking begins once you submit — an IRB number is generated at that
                    point.
                  </p>
                ) : null}

                {history.length > 0 ? (
                  <>
                    <hr className="divider" />
                    <h3 className="card-title" style={{ fontSize: '0.95rem' }}>
                      History
                    </h3>
                    <ul className="history-list">
                      {history.map((row, index) => (
                        <li key={`${row.StatusCode}-${row.ActionedOn}-${index}`}>
                          <span className="history-when">{formatDateTime(row.ActionedOn)}</span>
                          <span className="history-what">
                            <strong>{row.StatusDescription || row.StatusCode}</strong>
                            {row.ActionedBy ? ` · ${row.ActionedBy}` : ''}
                            {row.Remarks?.trim() ? (
                              <span className="history-remark">{row.Remarks}</span>
                            ) : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
            </section>

            <aside className="stack">
              <section className="card">
                <div className="card-header">
                  <div>
                    <h2 className="card-title">Your submission</h2>
                    <p className="card-subtitle">{data.title || 'Untitled research proposal'}</p>
                  </div>
                  <span className="badge badge--blue">
                    {satisfied.length}/{formSteps.length}
                  </span>
                </div>
                <div className="card-body">
                  <ul className="phase-list">
                    {PHASES.map((phase) => {
                      const steps = formSteps.filter((s) => s.phase === phase.n);
                      if (steps.length === 0) return null;
                      const done = steps.filter(
                        (s) => completedSteps.includes(s.id) || !isRequired(s.id)
                      ).length;
                      const pct = Math.round((done / steps.length) * 100);
                      return (
                        <li key={phase.n}>
                          <div className="phase-row">
                            <span className="phase-name">{phase.label}</span>
                            <span className="muted">
                              {done}/{steps.length}
                            </span>
                          </div>
                          <div
                            className="progress-bar"
                            role="progressbar"
                            aria-valuenow={pct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`${phase.label} completion`}
                          >
                            <span style={{ width: `${pct}%` }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  <hr className="divider" />

                  <ul className="meta-list">
                    <li>
                      <span className="meta-key">Review category</span>
                      <span className="meta-value">{data.reviewCategory || '—'}</span>
                    </li>
                    <li>
                      <span className="meta-key">Principal Investigator</span>
                      <span className="meta-value">{data.piName || '—'}</span>
                    </li>
                    <li>
                      <span className="meta-key">Co-investigators</span>
                      <span className="meta-value">{data.coInvestigators.length}</span>
                    </li>
                    <li>
                      <span className="meta-key">Grant requested</span>
                      <span className="meta-value">
                        {data.grantRequired === 'yes'
                          ? `PKR ${data.grantAmount || '—'}`
                          : data.grantRequired === 'no'
                            ? 'No'
                            : '—'}
                      </span>
                    </li>
                    <li>
                      <span className="meta-key">Revisions submitted</span>
                      <span className="meta-value">{status?.revisions ?? 0}</span>
                    </li>
                  </ul>

                  <button
                    type="button"
                    className="btn btn--secondary btn--block"
                    style={{ marginTop: 'var(--space-4)' }}
                    onClick={() => navigate(stepPath(WIZARD_STEPS[0].id))}
                  >
                    Open proposal
                  </button>

                  {submitted && sessionToken && status?.fileName ? (
                    <a
                      className="btn btn--ghost btn--block"
                      style={{ marginTop: 'var(--space-3)' }}
                      href={submissionPdfUrl(sessionToken, status.researchCode)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FileTextIcon size={16} aria-hidden="true" />
                      Submitted document (PDF)
                    </a>
                  ) : submitted ? (
                    <p className="muted" style={{ marginTop: 'var(--space-3)' }}>
                      The combined submission document is still being prepared. Your
                      proposal is with the committee either way — ask the IRB
                      Secretariat if it does not appear.
                    </p>
                  ) : null}
                </div>
              </section>

              <section className="card">
                <div className="card-body">
                  <div className="notice">
                    <MailIcon size={18} aria-hidden="true" />
                    <span>
                      Revisions requested by either committee are returned to you through
                      the portal. Queries: <a href={`mailto:${irbEmail}`}>{irbEmail}</a>.
                    </span>
                  </div>
                </div>
              </section>
            </aside>
          </div>
          </>
          ) : (
            <section className="card">
              <div className="card-body">
                <div className="notice">
                  <MailIcon size={18} aria-hidden="true" />
                  <span>
                    Your IRB number and the proposal's progress through the committees appear
                    here once you submit. Queries: <a href={`mailto:${irbEmail}`}>{irbEmail}</a>.
                  </span>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
